import { useChatStore, useChatInputStore, useCoworkerStore, useUIStore } from '@/store';
import { blockIfWorkModeDisabled } from '@/lib/workModeAvailability';
import { clearUseCaseDraftHandoff } from '@/lib/useCaseDraftHandoff';

export function isWorkConversationId(conversationId) {
  if (!conversationId) return false;
  const chat = useChatStore.getState().chats.find((entry) => String(entry.id) === String(conversationId));
  if (chat?.flags?.isCoworkerSession || chat?.coworkerSession) return true;
  const { taskIds, tasks } = useCoworkerStore.getState();
  return taskIds.some((taskId) => {
    const linked = tasks[taskId]?.conversationId || tasks[taskId]?.conversation;
    return linked && String(linked) === String(conversationId);
  });
}

/** True when the current surface is already Work (path or latched composer). */
export function shouldPreserveWorkComposer(pathname) {
  return isWorkComposerPath(pathname)
    || useCoworkerStore.getState().composerMode === 'coworker';
}

/** Open the home composer in Chat or Work mode. Work threads use this instead
 * of the dedicated /coworker hub. */
export function openHomeComposer(router, { work = false, replace = false } = {}) {
  // Kill switch: land on the Chat composer and say why, rather than opening a
  // Work composer whose send would be refused.
  const openWork = work && !blockIfWorkModeDisabled();
  useCoworkerStore.getState().setComposerMode(openWork ? 'coworker' : 'chat');
  // A leftover Files/editor panel sits at z-index 20 over the home Send button.
  useUIStore.getState().closeContextSidebar?.();
  const go = replace ? router.replace : router.push;
  go('/app');
}

/** Reset only the new composer; existing conversation drafts and Bots stay intact.
 * New Chat always starts in Chat — Work is an explicit opt-in. */
export function resetNewChatComposer() {
  clearUseCaseDraftHandoff();
  const composer = useChatInputStore.getState();
  composer.setContext('new');
  composer.clearInput(true);
  useCoworkerStore.getState().setComposerMode('chat');
}

/** Explicit New Chat actions start fresh in Chat; pass { work: true } for Work. */
export function openNewChatComposer(router, opts = {}) {
  resetNewChatComposer();
  openHomeComposer(router, { work: false, ...opts });
}

/** True when a drop or attach should stay on the Work composer, not the editor. */
export function isWorkComposerPath(pathname) {
  const path = String(pathname || '');
  const id = path.match(/\/c\/([^/?#]+)/)?.[1];
  return isWorkConversationId(id)
    || path === '/coworker'
    || path.startsWith('/coworker/')
    || ((path === '/app' || path === '/app/') && useCoworkerStore.getState().composerMode === 'coworker');
}

export function openHomeComposerFromPath(router, _pathname, opts = {}) {
  if (opts.work === true) {
    openNewChatComposer(router, opts);
    return;
  }
  // New Chat always starts in Chat, even from a Work thread. Pass { work: true }
  // (NEW_WORK) to open Work; only that explicit request earns the unavailable notice.
  openNewChatComposer(router, { ...opts, work: false });
}
